// MongoDB Connection Management
import mongoose from "mongoose";
// import { loadEnvConfig } from "./env-loader.js";
import { logError } from "./error-logger.js";

// Check if running on client or server
const isServer = typeof window === "undefined";

// Only import setupIndexes on the server side
let setupIndexesFunc;
if (isServer) {
  import("./db-indexes.js")
    .then((module) => {
      setupIndexesFunc = module.setupIndexes;
    })
    .catch((err) => {
      console.warn("⚠️ Failed to load db-indexes:", err.message);
    });
}

// Track connection state
let isConnected = false;
let connectionPromise = null;
let lastConnection = null;
let reconnectAttempts = 0;
let connectionError = null;
let connectionMonitorInterval = null;

// Serverless connection caching
let cachedConnection = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache for serverless

// Get environment variable with fallback
function getEnv(name, fallback = null) {
  return process.env[name] || fallback;
}

// Conditionally load environment variables if on the server
// This will prevent issues with Fast Refresh
if (typeof window === "undefined" && !process.env.NEXT_RUNTIME) {
  // Ensure environment variables are loaded
  try {
    // Use dynamic import for ES modules
    import("./env-loader.js")
      .then((module) => {
        module.loadEnvConfig();
      })
      .catch(() => {
        // Ignore if not available
      });
  } catch (e) {
    // Ignore if not available
  }
}

const connectionOptions = {
  maxPoolSize: 5, // Optimized for serverless - fewer connections
  minPoolSize: 1, // Minimal for serverless cold starts
  serverSelectionTimeoutMS: 3000, // Aggressive timeout for fast failure
  socketTimeoutMS: 15000, // Reduced to prevent function timeout
  connectTimeoutMS: 5000, // Fast connection timeout for serverless
  family: 4,
  heartbeatFrequencyMS: 60000, // Reduced heartbeat frequency
  retryWrites: true,
  w: "majority",
  directConnection: false,
  serverApi: {
    version: "1",
    strict: true,
    deprecationErrors: true,
  },
  // Critical serverless optimizations
  bufferCommands: false, // Don't buffer commands in serverless
  maxIdleTimeMS: 10000, // Short idle time for serverless
  authSource: "admin",
  // Additional serverless settings
  compressors: "zlib", // Reduce network overhead
  zlibCompressionLevel: 6, // Balanced compression
};

/**
 * Serverless-optimized MongoDB connection with caching
 * @param {boolean} force - Whether to force a new connection even if already connected
 * @param {number} timeoutMs - Maximum time to wait for connection (default 8s for serverless)
 * @returns {Promise<mongoose.Connection>} - Mongoose connection object
 */
export async function connectDB(force = false, timeoutMs = 8000) {
  if (!isServer) {
    console.log("⚠️ Database connection attempted client-side");
    return null;
  }

  // Check cached connection first (serverless optimization)
  if (!force && cachedConnection && cacheTimestamp) {
    const cacheAge = Date.now() - cacheTimestamp;
    if (cacheAge < CACHE_DURATION && mongoose.connection.readyState === 1) {
      console.log("⚡ Using cached MongoDB connection");
      return cachedConnection;
    }
  }

  // Check current connection state
  if (isConnected && !force && mongoose.connection.readyState === 1) {
    // Update cache
    cachedConnection = mongoose.connection;
    cacheTimestamp = Date.now();
    return mongoose.connection;
  }

  // Reuse existing connection promise to prevent duplicate connections
  if (connectionPromise && !force) {
    console.log("🔄 Reusing existing connection promise");
    return connectionPromise;
  }

  const uri = getEnv("MONGODB_URI");
  if (!uri) {
    throw new Error("MONGODB_URI environment variable is not set");
  }

  // Reduced retries for serverless - fail fast
  const maxRetries = 2;
  let lastError;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      if (attempt > 0) {
        // Shorter delays for serverless
        const delay = Math.min(500 * Math.pow(2, attempt), 2000);
        console.log(
          `🔄 Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms delay`
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      console.log(
        `📡 Connecting to MongoDB (timeout: ${timeoutMs}ms, attempt: ${attempt + 1})`
      );

      // Create connection promise with timeout
      const connectPromise = mongoose.connect(uri, connectionOptions);

      // Add connection timeout wrapper
      connectionPromise = Promise.race([
        connectPromise,
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error(`Connection timeout after ${timeoutMs}ms`)),
            timeoutMs
          )
        ),
      ]);

      await connectionPromise;

      isConnected = true;
      lastConnection = new Date();
      reconnectAttempts = 0;
      connectionError = null;

      // Cache the connection for serverless reuse
      cachedConnection = mongoose.connection;
      cacheTimestamp = Date.now();

      console.log("✅ MongoDB connected successfully (cached for reuse)");

      // Set up connection event listeners (lightweight for serverless)
      setupMongoEventListeners();

      return mongoose.connection;
    } catch (error) {
      lastError = error;
      connectionError = error;
      console.error(
        `❌ Connection attempt ${attempt + 1} failed:`,
        error.message
      );

      // Clear cache on connection failure
      cachedConnection = null;
      cacheTimestamp = null;

      if (
        error.code === "ETIMEOUT" ||
        error.message.includes("querySrv") ||
        error.message.includes("Connection timeout")
      ) {
        console.error("❗ Connection timeout or network issue detected.");
      }
      isConnected = false;
      connectionPromise = null; // Reset promise on failure
    }
  }

  console.error("❌ All connection attempts failed");

  // Clear failed connection cache
  cachedConnection = null;
  cacheTimestamp = null;

  // On failure after all retries, try one last time with minimal options for serverless
  console.warn(
    `Failed all ${maxRetries} attempts, trying serverless fallback connection...`
  );

  try {
    // Try one last time with serverless-optimized minimal options
    const minimalOptions = {
      serverSelectionTimeoutMS: 3000, // Very short for serverless
      socketTimeoutMS: 10000, // Short socket timeout
      connectTimeoutMS: 3000, // Fast connection timeout
      family: 4,
      bufferCommands: false,
      maxPoolSize: 1, // Single connection for fallback
    };

    console.log(`🔄 Final serverless connection attempt`);

    const fallbackPromise = Promise.race([
      mongoose.connect(uri, minimalOptions),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error(`Fallback connection timeout after 5000ms`)),
          5000
        )
      ),
    ]);

    await fallbackPromise;

    isConnected = true;
    lastConnection = new Date();
    reconnectAttempts = 0;
    connectionError = null;

    // Cache even the fallback connection
    cachedConnection = mongoose.connection;
    cacheTimestamp = Date.now();

    console.log("✅ MongoDB connected with serverless fallback");

    return mongoose.connection;
  } catch (finalError) {
    // Clear cache on final failure
    cachedConnection = null;
    cacheTimestamp = null;

    // If even the minimal connection fails, throw the original error
    throw lastError;
  }
}

// Add alias for backward compatibility with older code
export const connectToDatabase = connectDB;

/**
 * Lightweight connection monitor for serverless (reduced overhead)
 */
function setupConnectionMonitor() {
  // Skip connection monitoring in serverless production
  // Serverless functions are ephemeral - monitoring adds overhead
  if (process.env.VERCEL || process.env.NODE_ENV === "production") {
    console.log("⚡ Skipping connection monitor in serverless environment");
    return;
  }

  // Clear any existing monitor
  if (connectionMonitorInterval) {
    clearInterval(connectionMonitorInterval);
  }

  // Longer intervals for development stability only
  const monitorInterval = 120000; // 2 minutes

  connectionMonitorInterval = setInterval(async () => {
    // Skip if not server-side
    if (!isServer) return;

    try {
      // Check current connection state
      const currentState = mongoose.connection?.readyState;

      // Only attempt reconnection if completely disconnected
      if (currentState === 0) {
        console.log(
          `🔄 Connection monitor: DB disconnected, attempting reconnect`
        );

        // Clear stale cache before reconnecting
        cachedConnection = null;
        cacheTimestamp = null;

        try {
          await connectDB(true);
        } catch (error) {
          console.error(
            "❌ Connection monitor: Reconnection failed",
            error.message
          );
        }
      } else if (currentState === 1) {
        // Connection appears good, do a lightweight ping test
        try {
          const pingStart = Date.now();
          await mongoose.connection.db.admin().ping();
          const pingTime = Date.now() - pingStart;

          // Log slow responses and refresh cache on slow pings
          if (pingTime > 3000) {
            console.warn(
              `⚠️ Slow database ping: ${pingTime}ms - refreshing cache`
            );
            cachedConnection = null;
            cacheTimestamp = null;
          }
        } catch (pingError) {
          console.warn("❌ Connection monitor: Ping failed, clearing cache");
          // Clear cache on ping failure
          cachedConnection = null;
          cacheTimestamp = null;
        }
      }
    } catch (monitorError) {
      console.error("❌ Connection monitor error:", monitorError.message);
    }
  }, monitorInterval);

  console.log(
    `📡 Connection monitor started (${monitorInterval / 1000}s interval)`
  );
}

/**
 * Connect with retry logic using exponential backoff
 */
async function connectWithRetry(uri, retryCount = 0) {
  try {
    // Reset connection error
    connectionError = null;

    // Track reconnection attempt
    reconnectAttempts = retryCount;

    console.log(
      `📡 Connecting to MongoDB... (attempt ${reconnectAttempts + 1})`
    );

    // Connect to MongoDB
    await mongoose.connect(uri, connectionOptions);

    // Update connection state
    isConnected = true;
    lastConnection = new Date();
    reconnectAttempts = 0;

    console.log("✅ MongoDB connected successfully");

    // Set up database indexes for optimized queries
    if (process.env.NODE_ENV === "production" && isServer && setupIndexesFunc) {
      try {
        await setupIndexesFunc();
      } catch (indexError) {
        console.error("Error setting up database indexes:", indexError);
        // Don't fail connection due to index setup error
      }
    }

    // Set up connection event listeners for better monitoring
    setupMongoEventListeners();

    return mongoose.connection;
  } catch (error) {
    connectionError = error;
    isConnected = false;

    console.error(
      `❌ MongoDB connection failed (attempt ${retryCount + 1}):`,
      error.message
    );
    if (error.code === "ETIMEOUT" || error.message.includes("querySrv")) {
      console.error("❗ DNS querySrv timeout or network issue detected.");
    }

    // Maximum retry attempts (3 times with exponential backoff)
    const maxRetries = 3;

    if (retryCount < maxRetries) {
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.min(1000 * Math.pow(2, retryCount), 8000);

      console.log(`🔄 Retrying in ${delay / 1000} seconds...`);

      await new Promise((resolve) => setTimeout(resolve, delay));
      return connectWithRetry(uri, retryCount + 1);
    }

    console.error(
      `❌ Maximum retry attempts (${maxRetries}) reached. MongoDB connection failed.`
    );
    throw error;
  }
}

/**
 * Set up lightweight event listeners for serverless MongoDB connection
 */
function setupMongoEventListeners() {
  // Remove existing listeners if any
  mongoose.connection.removeAllListeners("disconnected");
  mongoose.connection.removeAllListeners("error");
  mongoose.connection.removeAllListeners("connected");

  // Listen for disconnection events
  mongoose.connection.on("disconnected", () => {
    console.log("❗ MongoDB disconnected");
    isConnected = false;
    // Clear cache on disconnection
    cachedConnection = null;
    cacheTimestamp = null;
  });

  mongoose.connection.on("error", (err) => {
    console.error("❗ MongoDB connection error:", err);
    logError("MongoDB Connection Error", err);
    isConnected = false;
    connectionError = err;
    // Clear cache on error
    cachedConnection = null;
    cacheTimestamp = null;
  });

  mongoose.connection.on("connected", () => {
    console.log("✅ MongoDB connection established");
    isConnected = true;
    connectionError = null;
    // Update cache on successful connection
    cachedConnection = mongoose.connection;
    cacheTimestamp = Date.now();
  });

  mongoose.connection.on("connected", () => {
    console.log("✅ MongoDB connected");
    isConnected = true;
    lastConnection = new Date();
  });
}

/**
 * Get current database connection status
 */
export function getConnectionStatus() {
  return {
    isConnected,
    lastConnection,
    reconnectAttempts,
    error: connectionError ? connectionError.message : null,
    readyState: mongoose.connection ? mongoose.connection.readyState : 0,
    host: mongoose.connection?.host || null,
    database: mongoose.connection?.name || null,
  };
}

/**
 * Check database connection status and attempt reconnection if needed
 * @returns {Promise<Object>} - Connection status object
 */
export async function checkDBConnection() {
  // If not connected at all, try to connect
  if (
    !isConnected ||
    !mongoose.connection ||
    mongoose.connection.readyState !== 1
  ) {
    try {
      await connectDB(true); // Force a new connection
      return {
        isConnected: true,
        status: "connected",
        message: "Connection established successfully",
        host: mongoose.connection?.host,
        database: mongoose.connection?.name,
      };
    } catch (error) {
      return {
        isConnected: false,
        status: "error",
        error: error.message,
        type: error.name,
      };
    }
  }

  // If connected, test with a ping
  try {
    const pingResult = await mongoose.connection.db.admin().ping();
    return {
      isConnected: pingResult.ok === 1,
      status: "connected",
      message: "Database is responsive",
      host: mongoose.connection?.host,
      database: mongoose.connection?.name,
      readyState: mongoose.connection?.readyState,
    };
  } catch (error) {
    return {
      isConnected: false,
      status: "error",
      error: error.message,
    };
  }
}

/**
 * Disconnect from MongoDB - Production Safe
 */
export async function disconnectDB() {
  // Skip disconnection in both production and development to prevent
  // connection churn and conflicts with Connection Manager
  console.log("⚠️ Skipping disconnect - Connection Manager handles lifecycle");
  return Promise.resolve();

  // Legacy disconnect code (commented out to prevent conflicts)
  // In serverless/Next.js environments, we shouldn't disconnect frequently
  // as it causes connection churn. Only disconnect on explicit shutdown.
  // if (process.env.NODE_ENV === "production") {
  //   console.log("⚠️ Production mode: Connection maintained for performance");
  //   return Promise.resolve();
  // }

  // if (!mongoose.connection || mongoose.connection.readyState === 0) {
  //   return Promise.resolve();
  // }

  // try {
  //   await mongoose.disconnect();
  //   isConnected = false;
  //   connectionPromise = null;
  //   console.log("✅ MongoDB disconnected successfully");
  // } catch (error) {
  //   console.error("❌ Error disconnecting from MongoDB:", error);
  //   logError("MongoDB Disconnect Error", error);
  //   throw error;
  // }
}

/**
 * Check database health with ping
 */
export async function checkDBHealth() {
  if (!isConnected || !mongoose.connection) {
    return { ok: false, error: "Not connected" };
  }

  try {
    const adminDb = mongoose.connection.db.admin();
    const result = await adminDb.ping();
    return {
      ok: result.ok === 1,
      responseTime: result.operationTime,
      connection: getConnectionStatus(),
    };
  } catch (error) {
    logError("MongoDB Health Check Failed", error);
    return {
      ok: false,
      error: error.message,
      connection: getConnectionStatus(),
    };
  }
}

/**
 * Ensure database connection is ready before executing operations
 * @param {number} retries - Number of retry attempts
 * @returns {Promise<boolean>} - True if connected, false otherwise
 */
export async function ensureConnection(retries = 3) {
  try {
    // Quick check if already connected
    if (mongoose.connection?.readyState === 1) {
      return true;
    }

    // If not connected, attempt to connect
    console.log(`🔄 Ensuring database connection (${retries} retries left)...`);

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        await connectDB(true);

        // Verify connection is truly ready
        if (mongoose.connection?.readyState === 1) {
          console.log(`✅ Database connection ensured on attempt ${attempt}`);
          return true;
        }

        // Wait before retry
        if (attempt < retries) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      } catch (error) {
        console.warn(
          `❌ Connection attempt ${attempt}/${retries} failed:`,
          error.message
        );
        if (attempt === retries) {
          throw error;
        }
      }
    }

    return false;
  } catch (error) {
    console.error("❌ Failed to ensure database connection:", error.message);
    return false;
  }
}

/**
 * Clear the connection cache (useful for forced reconnections)
 */
export function clearConnectionCache() {
  cachedConnection = null;
  cacheTimestamp = null;
  console.log("🗑️ Connection cache cleared");
}

/**
 * Get cached connection info for debugging
 */
export function getConnectionCacheInfo() {
  return {
    hasCachedConnection: !!cachedConnection,
    cacheAge: cacheTimestamp ? Date.now() - cacheTimestamp : null,
    maxCacheAge: CACHE_DURATION,
    connectionState: cachedConnection?.readyState || null,
  };
}

/**
 * Optimized connection function for high-frequency API calls
 * Uses aggressive caching for better serverless performance
 */
export async function fastConnectDB() {
  // For high-frequency calls, use cached connection if available
  if (cachedConnection && cacheTimestamp) {
    const cacheAge = Date.now() - cacheTimestamp;
    if (cacheAge < CACHE_DURATION && cachedConnection.readyState === 1) {
      return cachedConnection;
    }
  }

  // Fall back to regular connection with shorter timeout for speed
  return connectDB(false, 5000);
}

/**
 * Direct MongoDB database connection for timeout-resistant operations
 * Returns the database object directly for collection operations
 * @returns {Promise<{db: MongoDB.Db, connection: mongoose.Connection}>}
 */
export async function getDirectDatabase() {
  try {
    // Use cached connection if available and valid
    if (cachedConnection && cacheTimestamp) {
      const cacheAge = Date.now() - cacheTimestamp;
      if (cacheAge < CACHE_DURATION && cachedConnection.readyState === 1) {
        const db = cachedConnection.db;
        if (db) {
          return { db, connection: cachedConnection };
        }
      }
    }

    // Get fresh connection
    const connection = await connectDB(false, 6000);

    if (!connection || connection.readyState !== 1) {
      throw new Error("Database connection failed or invalid state");
    }

    const db = connection.db;
    if (!db) {
      throw new Error("Database object not available from connection");
    }

    // Verify database is accessible
    await db.admin().ping();

    return { db, connection };
  } catch (error) {
    console.error("❌ getDirectDatabase failed:", error);

    // Try emergency minimal connection
    try {
      console.log("🚨 Attempting emergency database connection...");

      // Clear cache and force new connection
      cachedConnection = null;
      cacheTimestamp = null;

      const emergencyConnection = await connectDB(true, 8000);
      const emergencyDb = emergencyConnection.db;

      if (!emergencyDb) {
        throw new Error(
          "Emergency connection failed to provide database object"
        );
      }

      return { db: emergencyDb, connection: emergencyConnection };
    } catch (emergencyError) {
      console.error("❌ Emergency database connection failed:", emergencyError);
      throw new Error(
        `Database connection completely failed: ${error.message}`
      );
    }
  }
}

// Default export for backward compatibility
export default connectDB;
